import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_EMAILS } from '../../../lib/graphql/queries/contacts';
import type { GQLMessageItem } from './useContact';

const PAGE_SIZE = 50;

interface GetContactEmailsData {
  contact: {
    contactId: string;
    emails: {
      items: GQLMessageItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactEmailsGQL = (
  contactId: string,
  dateRange?: { from: Date | null; to: Date | null },
  statuses?: string[],
  eventTypes?: string[],
  sortBy = 'created_at',
  sortOrder = 'DESC',
  directions?: string[],
) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery<GetContactEmailsData>(GET_CONTACT_EMAILS, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      ...(dateRange?.from && { dateFrom: dateRange.from.toISOString() }),
      ...(dateRange?.to && { dateTo: dateRange.to.toISOString() }),
      ...(statuses?.length && { statuses }),
      ...(eventTypes?.length && { eventTypes }),
      ...(directions?.length && { directions }),
      sortBy,
      sortOrder,
    },
    skip: !organization?.id || !contactId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.contact?.emails?.items ?? [];
  const total = data?.contact?.emails?.total ?? 0;
  const hasMore = data?.contact?.emails?.hasMore ?? false;

  const handleNextPage = useCallback(() => setCurrentPage((p) => p + 1), []);
  const handlePrevPage = useCallback(() => setCurrentPage((p) => Math.max(0, p - 1)), []);
  const goToPage = useCallback((page: number) => setCurrentPage(Math.max(0, page)), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);

  return {
    items,
    total,
    hasMore,
    loading,
    error,
    currentPage,
    pageSize: PAGE_SIZE,
    hasNext: hasMore,
    handleNextPage,
    handlePrevPage,
    goToPage,
    resetPage,
  };
};
